import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { useCloseAbuseCase } from '../../../api/abuse/closeCase'
import { updateOrder } from '../../../redux-api-bridge'
import { RoutePaths } from '../../../routes/routePaths'
import { getErrorMessages } from '../../../modules/MyPlan/selectors'

type OrderData = {
  products: Array<{
    id: number
    qty: number
    batch_id: string
    inventoryId: string
  }>
  freeProducts: Array<{
    id: number
    qty: number
    inventoryId: string
  }>
  timeSlot: {
    start: string
    end: string
  }
  orderId: number
  tip?: number
}

type ReconfirmOrderResult = {
  handleOrderUpdate: (orderData: OrderData) => Promise<void>
  isLoading: boolean
  error: {
    type: 'case' | 'order'
  } | null
  onError: () => void
}

export const useReconfirmOrder = (
  abuseCaseId: number,
  date: string
): ReconfirmOrderResult => {
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>()
  const { closeCase, loading: closeCaseLoading } = useCloseAbuseCase()
  const updateOrderError = useSelector(getErrorMessages)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<{
    type: 'case' | 'order'
  } | null>(null)

  const handleOrderUpdate = useCallback(
    async (orderData: OrderData) => {
      setIsLoading(true)
      setError(null)

      try {
        const { data } = await closeCase({ abuseCaseId })

        if (Boolean(data?.closeAbuseCase)) {
          const updatedOrder = {
            magentoOrderId: Number(orderData.orderId),
            start: orderData.timeSlot.start,
            end: orderData.timeSlot.end,
            products: orderData.products,
            freeProducts: orderData.freeProducts,
            tip: orderData.tip
          }

          const result = await dispatch(updateOrder(updatedOrder))

          if (result?.updateOrderSuccess) {
            window.location.reload()
          } else {
            setError({
              type: 'order'
            })
          }
        } else {
          throw new Error('An error occurred while processing your request.')
        }
      } catch (err) {
        setError({
          type: 'case'
        })
      } finally {
        setIsLoading(false)
      }
    },
    [abuseCaseId, closeCase, dispatch]
  )

  const redirectToMenu = () => {
    window.location.href = `${RoutePaths.MENU}?date=${date}`
  }

  useEffect(() => {
    if (updateOrderError) {
      setError({
        type: 'order'
      })
    }
  }, [updateOrderError])

  const onError =
    error?.type === 'order' ? redirectToMenu : () => setError(null)

  return {
    handleOrderUpdate,
    isLoading: isLoading || closeCaseLoading,
    error,
    onError
  }
}
